import { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Search, X } from "lucide-react";
import { SegmentedNav } from "@/ui";
import { useWorkspace } from "@/app/WorkspaceContext";
import { usePages } from "@/data/hooks";
import { AnalyticsScopeProvider } from "@/data/analyticsScope";
import { PANELS, RangeChips, isPanel, rangeLabel, type PanelKey } from "./parts";
import { SampleChip } from "./wiring";
import { SignalsSearchProvider } from "./searchContext";
import { PageFilter, type FilterablePage } from "./PageFilter";
import { Overview } from "./panels/Overview";
import { Realtime } from "./panels/Realtime";
import { Behavior } from "./panels/Behavior";
import { Acquisition } from "./panels/Acquisition";
import { Audience } from "./panels/Audience";
import { Funnels } from "./panels/Funnels";
import { Experiments } from "./panels/Experiments";
import { Events } from "./panels/Events";
import { Replays } from "./panels/Replays";
import { Heatmaps } from "./panels/Heatmaps";
import { Dashboard } from "./panels/Dashboard";

const VIEWS: Record<PanelKey, () => JSX.Element> = {
  overview: Overview,
  realtime: Realtime,
  behavior: Behavior,
  acquisition: Acquisition,
  audience: Audience,
  funnels: Funnels,
  experiments: Experiments,
  events: Events,
  replays: Replays,
  heatmaps: Heatmaps,
  dashboard: Dashboard,
};

export function SignalsScreen() {
  const nav = useNavigate();
  const { panel } = useParams();
  const { current, range, siteId, live, currentProjectId } = useWorkspace();
  const active: PanelKey = isPanel(panel) ? panel : "overview";
  const View = VIEWS[active];
  const isLive = !!siteId && live;

  // Per-panel row search. Lives here so it resets when you switch panels.
  const searchPlaceholder = (PANELS.find((p) => p.value === active) as { search?: string } | undefined)?.search;
  const [query, setQuery] = useState("");
  useEffect(() => setQuery(""), [active]);

  // Global "filter by page": scopes every panel to one page. Only meaningful for
  // built properties (which have builder pages). Picking a page switches the
  // analytics hooks to the page-scoped endpoints via AnalyticsScopeProvider.
  const pagesQ = usePages(currentProjectId);
  const filterablePages: FilterablePage[] = useMemo(
    () => (pagesQ.data ?? []).map((p) => ({ id: p.id, slug: p.slug, title: p.title })),
    [pagesQ.data],
  );
  const [pageId, setPageId] = useState<string | null>(null);
  const selectedPage = pageId ? filterablePages.find((p) => p.id === pageId) ?? null : null;

  return (
    <div className="space-y-6">
      <header className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <p className="eyebrow mb-1">Signals · {current.name}</p>
          <h1 className="text-[24px] font-semibold tracking-tight text-ink-strong md:text-[28px]">
            {selectedPage ? "One page, every signal" : "Everything that's working, merged"}
          </h1>
          <p className="mt-1 flex flex-wrap items-center gap-2 text-[13px] text-muted">
            <span>
              {isLive ? "First-party analytics" : "Sample data"} · {rangeLabel(range)}
              {selectedPage && (
                <>
                  {" · "}
                  <span className="font-mono text-ink">{selectedPage.slug}</span>
                </>
              )}
            </span>
            {!isLive && <SampleChip />}
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          {filterablePages.length > 0 && (
            <PageFilter pages={filterablePages} value={pageId} onChange={setPageId} />
          )}
          <RangeChips />
        </div>
      </header>

      {/* panel sub-nav — drives the /signals/:panel route */}
      <div className="sticky top-0 z-10 -mx-1 space-y-2 border-b border-hairline bg-canvas/80 px-1 pb-2 pt-1 backdrop-blur-sm">
        <SegmentedNav
          items={PANELS.map((p) => ({ value: p.value, label: p.label }))}
          value={active}
          onChange={(v) => nav(v === "overview" ? "/signals" : `/signals/${v}`)}
        />
        {searchPlaceholder && (
          <div className="relative">
            <Search
              className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-subtle"
              strokeWidth={1.75}
            />
            <input
              type="text"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder={searchPlaceholder}
              aria-label={searchPlaceholder}
              className="w-full rounded-full border border-hairline bg-paper py-2 pl-9 pr-9 text-[13px] text-ink outline-none transition-colors focus:border-accent/50 dark:bg-white/5"
            />
            {query && (
              <button
                type="button"
                aria-label="Clear search"
                onClick={() => setQuery("")}
                className="absolute right-2.5 top-1/2 grid h-6 w-6 -translate-y-1/2 place-items-center rounded-full text-subtle transition-colors hover:bg-paper-2 hover:text-ink dark:hover:bg-white/10"
              >
                <X className="h-3.5 w-3.5" strokeWidth={2} />
              </button>
            )}
          </div>
        )}
      </div>

      <AnalyticsScopeProvider value={pageId}>
        <SignalsSearchProvider value={query.trim().toLowerCase()}>
          <View />
        </SignalsSearchProvider>
      </AnalyticsScopeProvider>
    </div>
  );
}
